import { Trans, useLingui } from '@lingui/react/macro';
import { ArrowRight } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';

import { ComponentLocations } from 'src/analytics/trackingTypes';
import { useCheckoutAllowed } from 'src/api/queries';
import { LandingFooter } from 'src/apps/checkout/app/components/LandingFooter';
import { Button, Spinner } from 'src/components';
import { AppPage } from 'src/components/AppPage';
import { Highlight } from 'src/components/Highlight';
import { ValueProps } from 'src/components/Offers/ValueProps';
import { PromotionCheckoutLink } from 'src/components/PromotionCheckoutLink';
import { useFreeTrialDuration } from 'src/utils/freeTrialLength';
import { URLS } from 'src/utils/urls/urls';
import { valueProps } from 'src/utils/valueProps';

export const DmOfferLandingPage = () => {
  const { t } = useLingui();
  const checkoutAllowed = useCheckoutAllowed();
  const dmOffer = checkoutAllowed.data?.dmOffer;
  const trialDuration = useFreeTrialDuration(
    dmOffer?.allowed ? dmOffer.freeTrialLengthDays : 0
  );

  if (checkoutAllowed.isPending) {
    return <Spinner />;
  }

  if (checkoutAllowed.data?.dmOffer.allowed) {
    return (
      <>
        <AppPage.Content
          data-cy="dm-offer-landing"
          className="max-w-l2xl py-32 text-center ls:py-48 lm:py-64 ll:py-96"
        >
          <div className="flex flex-col items-center">
            <Typography
              variant="interface-sm"
              leadingNone
              uppercase
              className="my-0"
            >
              <Trans>Connect subscription</Trans>
            </Typography>

            <Typography
              variant="responsive-heading-xl"
              className="mt-8 mb-24 text-pretty lm:mt-24"
            >
              <Trans>
                Sync files to your{' '}
                <Highlight scale={300}>new paper tablet</Highlight>
              </Trans>
            </Typography>

            <Typography
              variant="body-md"
              className="mb-24 text-center lm:mb-40"
            >
              <Trans>
                Activate your free {trialDuration} Connect trial to sync your
                notes to the cloud and access them on multiple devices.
              </Trans>
            </Typography>

            <div className="mb-24 w-full text-left lm:mb-48">
              <ValueProps valueProps={valueProps.connect} />
            </div>

            <div className="flex flex-wrap items-center justify-center gap-24">
              <Button
                as="a"
                to={URLS.STORE_DM_OFFER_CHECKOUT}
                variant="primary"
                className="w-full ls:w-fit"
                data-cy="dm-offer-activate-button"
                analytics={{
                  action: 'Activate DM offer',
                  location: ComponentLocations.CHECKOUT.DM_OFFER_LANDING_MAIN,
                  text: t`Start free trial`,
                }}
              >
                <span>
                  <Trans>Start free trial</Trans>
                </span>{' '}
                <ArrowRight />
              </Button>
              <PromotionCheckoutLink
                componentLocation={
                  ComponentLocations.CHECKOUT.DM_OFFER_LANDING_MAIN
                }
              />
            </div>

            <Typography
              variant="body-sm"
              className="mt-24 max-w-lm text-pretty lm:mt-48"
            >
              <Trans>
                Your trial will automatically end after 30 days if you
                don&apos;t add a payment method.
              </Trans>
            </Typography>
          </div>
        </AppPage.Content>
        <LandingFooter
          heading={t`Why activate Connect?`}
          buttonText={t`Try it for free`}
          linkTo={URLS.STORE_DM_OFFER_CHECKOUT}
          componentLocation={
            ComponentLocations.CHECKOUT.DM_OFFER_LANDING_BOTTOM
          }
        />
      </>
    );
  }
};
